import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useEventBus } from 'shared/services/eventBus';
import { CommandHandler, COMMAND_TOPICS } from 'shared/services/messaging';
import { useNotification } from 'shared/contexts/UserNotificationProvider';
import { useTranslation } from 'react-i18next';

/**
 * useRobotCommands - Hook to send commands to robots via data channel
 *
 * Features:
 * - Command sending with validation
 * - Error handling and notifications
 * - Optional throttling
 * - Statistics tracking
 *
 * @param {Function} channelSendFn - Function to send data via channel
 * @param {Object} [options] - Hook options
 * @param {number} [options.throttle=0] - Throttle interval in ms
 * @param {boolean} [options.showErrors=true] - Show error notifications
 * @param {string} [options.channelName='chat'] - Data channel name
 * @returns {Object} Command sender functions and stats
 *
 * @example
 * const { sendTwist, sendJoy, sendCommand, stats } = useRobotCommands(channelSend, {
 *   throttle: 50 // Max 20 commands/sec
 * });
 *
 * // Send twist command
 * sendTwist({ linear: { x: 0.5 }, angular: { z: 0.2 } });
 *
 * // Send joystick command
 * sendJoy({ axes: [0, 0], buttons: [0, 0] });
 */
export const useRobotCommands = (channelSendFn, options = {}) => {
  const eventBus = useEventBus();
  const { notifyError } = useNotification();
  const { t } = useTranslation();
  const commandHandlerRef = useRef(null);

  // Initialize CommandHandler
  useEffect(() => {
    if (!channelSendFn) {
      return;
    }

    commandHandlerRef.current = new CommandHandler(channelSendFn, eventBus, {
      throttle: options.throttle || 0,
      channelName: options.channelName || 'chat',
      enableDebug: process.env.NODE_ENV === 'development',
      maxRetries: options.maxRetries || 0
    });

    // Subscribe to command errors
    const unsubscribe = eventBus.subscribe('command:error', (errorData) => {
      if (options.showErrors !== false) {
        notifyError(t('teleop.commands.sendFailed', { error: errorData.error }));
      }
    });

    // Cleanup
    return () => {
      if (commandHandlerRef.current) {
        commandHandlerRef.current.clear();
      }
      unsubscribe();
    };
  }, [channelSendFn, eventBus, notifyError, t, options.throttle, options.channelName, options.maxRetries, options.showErrors]);

  /**
   * Send generic command
   */
  const sendCommand = useCallback((topic, value) => {
    if (!commandHandlerRef.current) {
      if (options.showErrors !== false) {
        notifyError(t('teleop.commands.notInitialized'));
      }
      return false;
    }

    if (!channelSendFn) {
      if (options.showErrors !== false) {
        notifyError(t('teleop.commands.channelNotOpen'));
      }
      return false;
    }

    try {
      return commandHandlerRef.current.sendCommand(topic, value);
    } catch (error) {
      if (options.showErrors !== false) {
        notifyError(t('teleop.commands.error', { message: error.message }));
      }
      return false;
    }
  }, [channelSendFn, notifyError, t, options.showErrors]);

  /**
   * Send twist command (cmd_vel)
   */
  const sendTwist = useCallback((twist) => {
    return sendCommand(COMMAND_TOPICS.CMD_VEL, twist);
  }, [sendCommand]);

  /**
   * Send joystick command
   */
  const sendJoy = useCallback((joy) => {
    return sendCommand(COMMAND_TOPICS.JOY, joy);
  }, [sendCommand]);

  /**
   * Send keyboard command
   */
  const sendKeyboard = useCallback((keyboard) => {
    return sendCommand(COMMAND_TOPICS.KEYBOARD, keyboard);
  }, [sendCommand]);

  /**
   * Stop robot (send zero velocity)
   */
  const stopRobot = useCallback(() => {
    return sendTwist({
      linear: { x: 0, y: 0, z: 0 },
      angular: { x: 0, y: 0, z: 0 }
    });
  }, [sendTwist]);

  /**
   * Get command statistics
   */
  const getStats = useCallback(() => {
    if (!commandHandlerRef.current) {
      return {
        sent: 0,
        failed: 0,
        successRate: 0,
        byTopic: {}
      };
    }
    return commandHandlerRef.current.getCommandStats();
  }, []);

  return {
    sendCommand,
    sendTwist,
    sendJoy,
    sendKeyboard,
    stopRobot,
    getStats
  };
};

/**
 * useKeyboardControl - Hook for keyboard-based robot control
 *
 * @param {Function} channelSendFn - Function to send data via channel
 * @param {Object} [keyMap] - Key mappings to commands
 * @param {Object} [options] - Hook options
 * @returns {Object} Control functions
 *
 * @example
 * const { isEnabled, setEnabled } = useKeyboardControl(channelSend, {
 *   ArrowUp: () => ({ linear: { x: 0.5 }, angular: { z: 0 } }),
 *   ArrowDown: () => ({ linear: { x: -0.5 }, angular: { z: 0 } }),
 *   ArrowLeft: () => ({ linear: { x: 0 }, angular: { z: 0.5 } }),
 *   ArrowRight: () => ({ linear: { x: 0 }, angular: { z: -0.5 } })
 * });
 */
export const useKeyboardControl = (channelSendFn, keyMap = {}, options = {}) => {
  const { sendTwist, stopRobot } = useRobotCommands(channelSendFn, options);
  const [isEnabled, setEnabled] = useState(false);
  const activeKeysRef = useRef(new Set());

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const handleKeyDown = (event) => {
      if (activeKeysRef.current.has(event.key)) {
        return; // Key already pressed
      }

      activeKeysRef.current.add(event.key);

      const commandFn = keyMap[event.key];
      if (commandFn && typeof commandFn === 'function') {
        event.preventDefault();
        const twist = commandFn();
        sendTwist(twist);
      }
    };

    const handleKeyUp = (event) => {
      activeKeysRef.current.delete(event.key);

      if (keyMap[event.key]) {
        event.preventDefault();
        stopRobot();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      activeKeysRef.current.clear();
      stopRobot();
    };
  }, [isEnabled, keyMap, sendTwist, stopRobot]);

  return {
    isEnabled,
    setEnabled
  };
};

/**
 * useGamepadControl - Hook for gamepad-based robot control
 *
 * @param {Function} channelSendFn - Function to send data via channel
 * @param {Object} [options] - Hook options
 * @param {number} [options.updateRate=50] - Update rate in ms
 * @returns {Object} Control functions
 *
 * @example
 * const { isEnabled, setEnabled, gamepad } = useGamepadControl(channelSend, {
 *   updateRate: 50 // 20Hz
 * });
 */
export const useGamepadControl = (channelSendFn, options = {}) => {
  const { sendJoy } = useRobotCommands(channelSendFn, options);
  const [isEnabled, setEnabled] = useState(false);
  const [gamepad, setGamepad] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const updateRate = options.updateRate || 50;

    intervalRef.current = setInterval(() => {
      const gamepads = navigator.getGamepads();
      const activeGamepad = gamepads[0]; // Use first gamepad

      if (activeGamepad) {
        setGamepad(activeGamepad);

        // Send joystick command
        sendJoy({
          axes: Array.from(activeGamepad.axes),
          buttons: Array.from(activeGamepad.buttons).map(b => b.value)
        });
      }
    }, updateRate);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isEnabled, sendJoy, options.updateRate]);

  return {
    isEnabled,
    setEnabled,
    gamepad
  };
};

export default useRobotCommands;
